import classnames from 'classnames';
import type { FunctionComponent } from 'react';

import { CurrencyType, Price } from '@/core/tamagoshi/Price/Price';
import { Tag } from '@/core/tamagoshi/Tag/Tag';
import { Link } from '@/domains/core/routing/components/Link';
import {
  getCurrency,
  getMarket,
  getPlatform,
} from '@/domains/core/settings/utils';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useCashbackOfferQuery } from '@/domains/productDiscovery/CommercialAnimation/services/graphql/CashbackOffer/generated/cashbackOffer.query.generated';
import { GTM_CLICK_ON_CASHBACK_OFFER } from '@/domains/productDiscovery/CommercialAnimation/services/tracking/clickOnCashbackOffer';
import { type Market, type Platform } from '@/generated/graphql/types';

import { learnMoreText, refundText } from './translations';

import styles from '@/domains/productDiscovery/CommercialAnimation/components/CashbackOfferTag/CashbackOfferTag.module.scss';

export interface CashbackOfferProps {
  className?: string;
  productId: string;
}

export const CashbackOfferTag: FunctionComponent<CashbackOfferProps> = ({
  className,
  productId,
}) => {
  const CURRENCY = getCurrency();

  const [{ data, fetching }] = useCashbackOfferQuery({
    variables: {
      market: getMarket().toUpperCase() as Market,
      platform: getPlatform().toUpperCase() as Platform,
      productId,
    },
  });

  if (fetching || !data?.cashbackOffer) return null;

  const {
    cashbackOffer: { displayAmount, displayLabel, link },
  } = data;

  const tagClassName = classnames(styles.tag, className);

  return (
    <Tag color="lightBlue" className={tagClassName}>
      {displayLabel ? (
        <span>{displayLabel}</span>
      ) : (
        <>
          {refundText.format({
            amount: (
              <Price
                key="cashback"
                value={displayAmount || 0}
                currency={CurrencyType[CURRENCY]}
                minimumFractionDigits={0}
                maximumFractionDigits={2}
              />
            ),
          })}
        </>
      )}
      {link.url && (
        <Link
          href={link.url}
          data-testid="link"
          target="_blank"
          onClick={() => Gtm.push(GTM_CLICK_ON_CASHBACK_OFFER())}
        >
          <span className={styles.linkText}>{learnMoreText}</span>
        </Link>
      )}
    </Tag>
  );
};
